import { useEffect, useState } from 'react';
import { cancelBillingAtPeriodEnd, getBilling, openBillingPortal } from '../api/billing';
import { getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { canManageBilling } from '../auth/permissions';
import { Brand } from '../components/Brand';
import { Icon } from '../components/ui/Icon';
import type { BillingOverview } from '../types/billing';
import { formatBillingDate } from '../utils/billingView';
import { redirectTo } from '../utils/externalRedirect';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';

// Fase 25A: pantalla neutral para una cuenta suspendida/archivada. Única acción operativa: cerrar sesión.
// Preserva el lenguaje visual 24B.
// ONB-007 (revisión independiente, H1): si el negocio paga un plan (facturación encendida), la persona dueña
// también ve que su renovación quedó detenida y puede abrir el portal de pagos (ver facturas, forma de pago).
// El servidor solo le permite eso con la cuenta así: ni contratar ni reanudar.
// ONB-008 (revisión 6, LOW-4): si la renovación todavía no está detenida (la retención automática falló o aún no
// se aplica), la persona dueña puede detenerla aquí: el portal ya no cancela y el servidor sí lo permite.
export function AccountBlockedPage({ archived = false }: { archived?: boolean }) {
  const { logout, user, stores } = useAuth();
  const owner = canManageBilling(user?.role);
  const timeZone = stores[0]?.timezone ?? DEFAULT_STORE_TIMEZONE;
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState<BillingOverview | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const onLogout = async () => { if (busy) return; setBusy(true); await logout(); };

  useEffect(() => {
    if (!owner) return undefined;
    let alive = true;
    // Facturación apagada (404) o sin plan contratado: no se muestra nada.
    getBilling().then((view) => { if (alive) setBilling(view); }).catch(() => undefined);
    return () => { alive = false; };
  }, [owner]);

  const subscription = billing?.billingMode === 'SELF_SERVE' ? billing.subscription : null;
  const endsOn = formatBillingDate(billing?.cancelEffectiveAt, timeZone);
  const canCancel = Boolean(subscription && !billing?.cancelEffectiveAt && billing?.actions.includes('CANCEL_AT_PERIOD_END'));

  const onCancel = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      setBilling(await cancelBillingAtPeriodEnd());
      setConfirmCancel(false);
    } catch (error) {
      setCancelError(getErrorMessage(error, 'No pudimos detener la renovación. Intenta de nuevo.'));
      // La retención automática pudo aplicarse a la vez (revisión 6b, R6): se vuelve a leer el estado y, si la
      // renovación ya quedó detenida, el error ya no aplica (revisión 6c, INFO-d).
      getBilling().then((view) => {
        setBilling(view);
        if (view.cancelEffectiveAt) setCancelError(null);
      }).catch(() => undefined);
    } finally {
      setCancelBusy(false);
    }
  };

  const onPortal = async () => {
    if (portalBusy) return;
    setPortalBusy(true);
    setPortalError(null);
    try {
      redirectTo((await openBillingPortal('DEFAULT')).url);
    } catch (error) {
      setPortalError(getErrorMessage(error, 'No pudimos abrir el portal de pagos. Intenta de nuevo.'));
      setPortalBusy(false);
    }
  };

  return (
    <main className="account-blocked">
      <div className="account-blocked__card">
        <div className="account-blocked__brand"><Brand /></div>
        <span className="account-blocked__icon"><Icon name="warning" size={30} /></span>
        <h1>{archived ? 'Esta cuenta está archivada.' : 'Esta cuenta está suspendida.'}</h1>
        <p>El acceso a la operación de esta cuenta no está disponible por ahora. Si crees que es un error, contacta al administrador de tu plataforma.</p>
        {subscription && (
          <section aria-label="Tu plan">
            <p>
              {endsOn
                ? `La renovación de tu plan está detenida: no se te cobrará ningún periodo nuevo. Tu plan termina el ${endsOn}.`
                : canCancel
                  ? 'Tu plan sigue registrado. Puedes revisar tus pagos en el portal o cancelar tu plan al final del periodo.'
                  : 'Tu plan sigue registrado. Puedes revisar tus pagos en el portal.'}
            </p>
            <button className="button button--secondary" disabled={portalBusy} onClick={() => void onPortal()} type="button">
              {portalBusy ? 'Abriendo…' : 'Ver pagos y facturas'}
            </button>
            {portalError && <p role="alert">{portalError}</p>}
            {canCancel && !confirmCancel && (
              <button className="button button--secondary" onClick={() => setConfirmCancel(true)} type="button">Cancelar al final del periodo</button>
            )}
            {canCancel && confirmCancel && (
              <div role="group" aria-label="Confirmar la cancelación">
                <p>No se te cobrará ningún periodo nuevo. Tu información sigue guardada.</p>
                <button className="button button--secondary" disabled={cancelBusy} onClick={() => setConfirmCancel(false)} type="button">Mantener mi plan</button>
                <button className="button button--primary" disabled={cancelBusy} onClick={() => void onCancel()} type="button">
                  {cancelBusy ? 'Programando…' : 'Cancelar al final del periodo'}
                </button>
              </div>
            )}
            {cancelError && <p role="alert">{cancelError}</p>}
          </section>
        )}
        <button className="button button--primary" disabled={busy} onClick={onLogout} type="button">
          {busy ? 'Saliendo…' : 'Cerrar sesión'}
        </button>
      </div>
    </main>
  );
}
